import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import {
  ISalesforceObjectMetaField,
  ISalesforceObjectSummary,
  SalesforceMetadataService
} from '../../../services/salesforce/salesforce-metadata.service';
import { ISalesforceConfig, ISalesforceDisplayTemplates, ISalesforceFieldMapping, ISalesforceObjectMapping, ISalesforceRelatedObject } from '../adv2-config';
import { ISalesforcePathBuilderPickerApi } from './salesforce-path-builder-picker.component';

interface ISalesforcePathOption {
  segment: string;
  label: string;
  type?: string;
  sourceType?: string;
  associatedEntity?: string;
}

interface ISalesforceFlyoutColumn {
  targetObject: string;
  options: ISalesforcePathOption[];
  loading: boolean;
  error: string | null;
  searchTerm: string;
  selectedRelation: ISalesforcePathOption | null;
}

interface ISalesforcePathBuilderState {
  selections: (ISalesforcePathOption | null)[];
  optionsByLevel: ISalesforcePathOption[][];
  objectNameByLevel: string[];
  loadingByLevel: boolean[];
  errorsByLevel: (string | null)[];
  searchByLevel: string[];
  openByLevel: boolean[];
  flyoutColumnsByLevel: ISalesforceFlyoutColumn[][];
  flyoutCanScrollLeftByLevel: boolean[];
  flyoutCanScrollRightByLevel: boolean[];
  invalidSavedPath: boolean;
}

interface ISalesforceRelationOption {
  segment: string;
  label: string;
}

interface ISalesforceRelatedEntityCandidate {
  objectName: string;
  relationSegments: ISalesforceRelationOption[];
}

interface ISalesforceRelatedEntityState {
  loading: boolean;
  error: string | null;
  relationOptions: ISalesforcePathOption[];
}

@Component({
  selector: 'app-salesforce-mapping',
  templateUrl: './salesforce-mapping.component.html',
  styleUrls: ['./salesforce-mapping.component.scss'],
  standalone: false,
})
export class SalesforceMappingComponent implements OnInit, OnDestroy {
  @ViewChildren('mappingCard') mappingCards!: QueryList<ElementRef<HTMLElement>>;
  @Input() set config(value: ISalesforceConfig) {
    const shouldResetPathBuilder = this.shouldResetPathBuilderState(this._config, value);
    this._config = this.cloneConfig(value);
    if (shouldResetPathBuilder) {
      this.pathBuilderState.clear();
      this.expandedFieldKeys.clear();
    }
    if (!this._config.objects.length) {
      this.addMapping(false);
    }
    if (this.expandedMappingIndex >= this._config.objects.length) {
      this.expandedMappingIndex = Math.max(0, this._config.objects.length - 1);
    }
    this.hydrateRelatedEntitySelections();
    this.cdr.markForCheck();
    this.updateValidity();
  }
  @Output() configChange = new EventEmitter<ISalesforceConfig>();
  @Output() validityChange = new EventEmitter<boolean>();

  availableObjects: ISalesforceObjectSummary[] = [];
  isLoading = false;
  expandedMappingIndex = -1;
  readonly maxAssociatedEntityDepth = 2;
  readonly pathDropdownPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];
  // Track search text per row so each dropdown filters independently.
  dropdownSearchTerms: Map<number, string> = new Map();
  isValid = true;
  private subscriptions = new Subscription();
  private _config: ISalesforceConfig = { objects: [] };
  private fieldOptionsCache = new Map<string, ISalesforcePathOption[]>();
  private fieldOptionsRequests = new Map<string, Observable<ISalesforcePathOption[]>>();
  private knownObjectLookup = new Map<string, string>();
  private pathBuilderState = new Map<string, ISalesforcePathBuilderState>();
  private relatedEntityState = new Map<string, ISalesforceRelatedEntityState>();
  private selectedRelatedEntityPaths = new Map<string, string>();
  private hydratingPathBuilderKeys = new Set<string>();
  private expandedFieldKeys = new Set<string>();
  readonly pathBuilderApi: ISalesforcePathBuilderPickerApi = {
    getSelectedPathSegments: (mappingIndex, fieldIndex) => this.getSelectedPathSegments(mappingIndex, fieldIndex),
    isBuiltPathInvalid: (mappingIndex, fieldIndex) => this.isBuiltPathInvalid(mappingIndex, fieldIndex),
    getPathLevels: (mappingIndex, fieldIndex) => this.getPathLevels(mappingIndex, fieldIndex),
    getPathLevelLabel: (mappingIndex, fieldIndex, level) => this.getPathLevelLabel(mappingIndex, fieldIndex, level),
    getPathLevelSearchTerm: (mappingIndex, fieldIndex, level) => this.getPathLevelSearchTerm(mappingIndex, fieldIndex, level),
    getSelectedPathLevelLabel: (mappingIndex, fieldIndex, level) => this.getSelectedPathLevelLabel(mappingIndex, fieldIndex, level),
    onPathLevelSearchChange: (mappingIndex, fieldIndex, level, term) =>
      this.onPathLevelSearchChange(mappingIndex, fieldIndex, level, term),
    setPathLevelOpen: (mappingIndex, fieldIndex, level, open) => this.setPathLevelOpen(mappingIndex, fieldIndex, level, open),
    closePathLevel: (mappingIndex, fieldIndex, level) => this.closePathLevel(mappingIndex, fieldIndex, level),
    isPathLevelOpen: (mappingIndex, fieldIndex, level) => this.isPathLevelOpen(mappingIndex, fieldIndex, level),
    getPathLevelDomKey: (mappingIndex, fieldIndex, level) => this.getPathLevelDomKey(mappingIndex, fieldIndex, level),
    getFlyoutDropdownWidth: (mappingIndex, fieldIndex, level) => this.getFlyoutDropdownWidth(mappingIndex, fieldIndex, level),
    onFlyoutScroll: (mappingIndex, fieldIndex, level) => this.onFlyoutScroll(mappingIndex, fieldIndex, level),
    getFlyoutBreadcrumb: (mappingIndex, fieldIndex, level) => this.getFlyoutBreadcrumb(mappingIndex, fieldIndex, level),
    canFlyoutScrollLeft: (mappingIndex, fieldIndex, level) => this.canFlyoutScrollLeft(mappingIndex, fieldIndex, level),
    canFlyoutScrollRight: (mappingIndex, fieldIndex, level) => this.canFlyoutScrollRight(mappingIndex, fieldIndex, level),
    scrollFlyoutColumns: (mappingIndex, fieldIndex, level, direction) =>
      this.scrollFlyoutColumns(mappingIndex, fieldIndex, level, direction),
    getFlyoutColumns: (mappingIndex, fieldIndex, level) => this.getFlyoutColumns(mappingIndex, fieldIndex, level),
    trackByFlyoutColumn: (index, column) => this.trackByFlyoutColumn(index, column),
    onFlyoutColumnSearchChange: (mappingIndex, fieldIndex, level, columnIndex, term) =>
      this.onFlyoutColumnSearchChange(mappingIndex, fieldIndex, level, columnIndex, term),
    getFilteredFlyoutColumnOptions: (mappingIndex, fieldIndex, level, columnIndex) =>
      this.getFilteredFlyoutColumnOptions(mappingIndex, fieldIndex, level, columnIndex),
    trackByPathOption: (index, option) => this.trackByPathOption(index, option),
    onFlyoutOptionHover: (mappingIndex, fieldIndex, level, columnIndex, option) =>
      this.onFlyoutOptionHover(mappingIndex, fieldIndex, level, columnIndex, option),
    onFlyoutOptionSelect: (mappingIndex, fieldIndex, level, columnIndex, option) =>
      this.onFlyoutOptionSelect(mappingIndex, fieldIndex, level, columnIndex, option),
    isRelationOptionSelectedForColumn: (mappingIndex, fieldIndex, level, columnIndex, option) =>
      this.isRelationOptionSelectedForColumn(mappingIndex, fieldIndex, level, columnIndex, option),
    formatPathOption: (option) => this.formatPathOption(option),
    hasSelectableFlyoutOptions: (mappingIndex, fieldIndex, level, columnIndex) =>
      this.hasSelectableFlyoutOptions(mappingIndex, fieldIndex, level, columnIndex),
    isAtAssociatedEntityDepthCap: (mappingIndex, fieldIndex, level, columnIndex) =>
      this.isAtAssociatedEntityDepthCap(mappingIndex, fieldIndex, level, columnIndex),
    getPathLevelLoading: (mappingIndex, fieldIndex, level) => this.getPathLevelLoading(mappingIndex, fieldIndex, level),
    getPathLevelError: (mappingIndex, fieldIndex, level) => this.getPathLevelError(mappingIndex, fieldIndex, level),
    stepBackPath: (mappingIndex, fieldIndex) => this.stepBackPath(mappingIndex, fieldIndex),
    canStepBackPath: (mappingIndex, fieldIndex) => this.canStepBackPath(mappingIndex, fieldIndex),
    clearPathPicker: (mappingIndex, fieldIndex) => this.clearPathPicker(mappingIndex, fieldIndex),
  };

  constructor(
    private metadataService: SalesforceMetadataService,
    private cdr: ChangeDetectorRef
  ) {}

  get mappings(): ISalesforceObjectMapping[] {
    return this._config.objects;
  }

  ngOnInit(): void {
    this.isLoading = true;
    const sub = this.metadataService.getObjects().subscribe({
      next: (objects) => {
        this.availableObjects = (objects ?? [])
          .map((obj: any) => {
            const objectName = this.getObjectApiName(obj);
            return {
              name: objectName,
              apiName: objectName,
              label: obj?.label,
              custom: obj?.custom,
            } as ISalesforceObjectSummary;
          })
          .filter((obj) => Boolean(this.getObjectApiName(obj)));
        this.rebuildKnownObjectLookup();
        this.preloadRelatedEntityCandidates();

        this.isLoading = false;
      },
      error: () => {
        this.availableObjects = [];
        this.knownObjectLookup.clear();
        this.isLoading = false;
      },
    });
    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getSearchTerm(index: number): string {
    return this.dropdownSearchTerms.get(index) ?? '';
  }

  onAutocompleteInput(index: number, term: string | null | undefined): void {
    this.dropdownSearchTerms.set(index, term ?? '');
  }

  onAutocompleteSelected(index: number, event: MatAutocompleteSelectedEvent): void {
    const selection = (event.option?.value as string) ?? '';
    const summary = this.availableObjects.find((obj) => this.getObjectApiName(obj) === selection);
    this.dropdownSearchTerms.set(index, '');
    this.selectObject(index, selection, summary?.label ?? selection);
  }

  getFilteredObjects(index: number): ISalesforceObjectSummary[] {
    const searchTerm = this.getSearchTerm(index).trim().toLowerCase();
    if (!searchTerm) {
      return this.availableObjects;
    }
    return this.availableObjects.filter((option) => {
      const objectName = this.getObjectApiName(option).toLowerCase();
      const api = objectName;
      const name = objectName;
      const label = option.label?.toLowerCase() ?? '';
      const labelWithName = this.getOptionDisplay(option).toLowerCase();
      return api.includes(searchTerm) || name.includes(searchTerm) || label.includes(searchTerm) || labelWithName.includes(searchTerm);
    });
  }

  getObjectDisplay(objectName?: string): string {
    if (!objectName) {
      return '';
    }

    const summary = this.availableObjects.find((obj) => this.getObjectApiName(obj) === objectName);
    if (!summary) {
      return objectName;
    }

    return this.getOptionDisplay(summary);
  }

  // Used by mat-autocomplete displayWith to show the selected value in the input.
  objectDisplay = (value?: string | null): string => this.getObjectDisplay(value ?? '');

  selectObject(index: number, objectName: string | undefined, label?: string): void {
    if (!objectName) {
      return;
    }

    const mapping = this._config.objects[index];
    mapping.objectName = objectName;
    mapping.id = objectName;
    mapping.label = label ?? objectName;
    mapping.display = mapping.display ?? this.getEmptyDisplayTemplates();
    if (!mapping.display.primary?.trim()) {
      mapping.display.primary = '{{Name}}';
    }

    if (!mapping.fields.length) {
      this.addField(index, false);
    }

    this.pathBuilderState.clear();
    this.clearRelatedEntitySelectionsForMapping(index);
    this.ensureRelatedEntityCandidatesLoaded(objectName);
    this.cdr.detectChanges();
    this.emitChange();
    this.updateValidity();
  }

  addMapping(emitChange = true): void {
    const mapping: ISalesforceObjectMapping = {
      id: '',
      label: '',
      objectName: '',
      enabled: true,
      fields: [],
      display: this.getEmptyDisplayTemplates(),
    };
    this._config.objects = [...this._config.objects, mapping];
    this.pathBuilderState.clear();
    if (emitChange) {
      this.expandedMappingIndex = this._config.objects.length - 1;
      this.scrollToLastMapping();
      this.emitChange();
    }
    this.updateValidity();
  }

  removeMapping(index: number): void {
    if (this._config.objects.length === 1) {
      return;
    }
    this._config.objects = this._config.objects.filter((_, i) => i !== index);
    if (this.expandedMappingIndex >= this._config.objects.length) {
      this.expandedMappingIndex = Math.max(0, this._config.objects.length - 1);
    }
    this.pathBuilderState.clear();
    this.emitChange();
    this.updateValidity();
  }

  addField(mappingIndex: number, emitChange = true): void {
    const field: ISalesforceFieldMapping = {
      label: '',
      path: '',
      dataType: 'text',
    };
    const mapping = this._config.objects[mappingIndex];
    mapping.fields = [...mapping.fields, field];
    const fieldIndex = mapping.fields.length - 1;
    this.expandedFieldKeys.add(this.getFieldPanelKey(mappingIndex, fieldIndex));
    this.pathBuilderState.clear();
    if (emitChange) {
      this.emitChange();
    }
    this.updateValidity();
  }

  removeField(mappingIndex: number, fieldIndex: number): void {
    const mapping = this._config.objects[mappingIndex];
    mapping.fields = mapping.fields.filter((_, i) => i !== fieldIndex);
    this.rebuildExpandedFieldState(mappingIndex, mapping.fields.length);
    this.pathBuilderState.clear();
    this.emitChange();
    this.updateValidity();
  }

  isMappingExpanded(index: number): boolean {
    return this.expandedMappingIndex === index;
  }

  toggleMapping(index: number): void {
    this.expandedMappingIndex = this.expandedMappingIndex === index ? -1 : index;
  }

  getMappingSummary(index: number): string {
    const mapping = this._config.objects[index];
    const enabled = mapping?.enabled ? 'Enabled' : 'Disabled';
    const fieldCount = mapping?.fields?.length ?? 0;
    return `${enabled} • ${fieldCount} field${fieldCount === 1 ? '' : 's'}`;
  }

  getMappingTitle(index: number): string {
    const mapping = this._config.objects[index];
    return this.getObjectDisplay(mapping?.objectName) || 'Select Salesforce object';
  }

  getFieldPanelKey(mappingIndex: number, fieldIndex: number): string {
    return `${mappingIndex}:${fieldIndex}`;
  }

  isFieldExpanded(mappingIndex: number, fieldIndex: number): boolean {
    return this.expandedFieldKeys.has(this.getFieldPanelKey(mappingIndex, fieldIndex));
  }

  toggleField(mappingIndex: number, fieldIndex: number): void {
    const key = this.getFieldPanelKey(mappingIndex, fieldIndex);
    if (this.expandedFieldKeys.has(key)) {
      this.expandedFieldKeys.delete(key);
      return;
    }
    this.expandedFieldKeys.add(key);
  }

  getFieldSummary(mappingIndex: number, fieldIndex: number): string {
    const mapping = this._config.objects[mappingIndex];
    const field = mapping?.fields?.[fieldIndex];
    const path = field?.path?.trim() || 'No path selected';
    const type = field?.dataType || 'text';
    return `${path} • ${type}`;
  }

  getFieldTitle(mappingIndex: number, fieldIndex: number): string {
    const mapping = this._config.objects[mappingIndex];
    const field = mapping?.fields?.[fieldIndex];
    const rawPath = field?.path?.trim() ?? '';
    if (!rawPath) {
      return 'New field';
    }

    const state =
      this.pathBuilderState.get(this.getPathBuilderKey(mappingIndex, fieldIndex)) ??
      this.getPathBuilderState(mappingIndex, fieldIndex);
    const lastSelected = state?.selections?.filter((option): option is ISalesforcePathOption => Boolean(option)).slice(-1)[0];
    const metadataLabel = lastSelected?.label?.trim() || '';
    if (metadataLabel) {
      return metadataLabel;
    }
    if (state?.invalidSavedPath) {
      return 'Invalid field path';
    }
    return 'Loading field label...';
  }

  getTemplateFieldPaths(mappingIndex: number): string[] {
    const mapping = this._config.objects[mappingIndex];
    return (mapping?.fields ?? [])
      .map((field) => field.path?.trim() ?? '')
      .filter((path) => Boolean(path));
  }

  insertTemplateToken(mappingIndex: number, templateKey: 'primary' | 'secondary', path: string): void {
    const mapping = this._config.objects[mappingIndex];
    if (!mapping || !path?.trim()) {
      return;
    }

    mapping.display = mapping.display ?? this.getEmptyDisplayTemplates();
    const current = (mapping.display[templateKey] ?? '').trim();
    const token = `{{${path}}}`;
    if (current.includes(token)) {
      return;
    }

    mapping.display[templateKey] = current ? `${current} ${token}` : token;
    this.onModelChange();
  }

  getTemplateInvalidTokens(mappingIndex: number, templateKey: 'primary' | 'secondary'): string[] {
    const mapping = this._config.objects[mappingIndex];
    if (!mapping) {
      return [];
    }

    const template = mapping.display?.[templateKey] ?? '';
    const tokens = this.extractTemplateTokens(template);
    if (!tokens.length) {
      return [];
    }

    const validPaths = new Set(this.getTemplateFieldPaths(mappingIndex));
    const invalid = tokens.filter((token) => !validPaths.has(token));
    return Array.from(new Set(invalid));
  }

  hasTemplateInvalidTokens(mappingIndex: number, templateKey: 'primary' | 'secondary'): boolean {
    return this.getTemplateInvalidTokens(mappingIndex, templateKey).length > 0;
  }

  getTemplateInvalidTokensText(mappingIndex: number, templateKey: 'primary' | 'secondary'): string {
    const invalid = this.getTemplateInvalidTokens(mappingIndex, templateKey);
    return invalid.map((token) => `{{${token}}}`).join(', ');
  }

  hasMappingTemplateInvalidTokens(mappingIndex: number): boolean {
    return this.getMappingInvalidTemplateTokens(mappingIndex).length > 0;
  }

  hasMappingInvalidFieldPaths(mappingIndex: number): boolean {
    const mapping = this._config.objects[mappingIndex];
    return (mapping?.fields ?? []).some(
      (field, fieldIndex) => !field.path?.trim() || this.isBuiltPathInvalid(mappingIndex, fieldIndex)
    );
  }

  getMappingTemplateInvalidTokensText(mappingIndex: number): string {
    return this.getMappingInvalidTemplateTokens(mappingIndex)
      .map((token) => `{{${token}}}`)
      .join(', ');
  }

  onModelChange(): void {
    this.emitChange();
    this.updateValidity();
  }

  onToggleChange(): void {
    this.emitChange();
    this.updateValidity();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByFieldIndex(index: number): number {
    return index;
  }

  trackByOptionName = (index: number, option: ISalesforceObjectSummary): string => this.getObjectApiName(option) || index.toString();

  getOptionDisplay(option?: ISalesforceObjectSummary): string {
    if (!option) {
      return '';
    }

    const apiName = option.apiName?.trim();
    const fallbackName = option.objectName?.trim() || option.name?.trim();
    const name = apiName || fallbackName || '';
    const label = option.label?.trim();
    return label ? `${label} (${name})` : name;
  }

  getPossibleRelatedEntities(mappingIndex: number): ISalesforceRelatedEntityCandidate[] {
    const mapping = this._config.objects[mappingIndex];
    const objectName = mapping?.objectName?.trim();
    if (!objectName) {
      return [];
    }

    const normalizedObjectName = this.getKnownObjectName(objectName) ?? objectName;
    this.ensureRelatedEntityCandidatesLoaded(normalizedObjectName);
    const enabledObjects = new Set(
      this._config.objects
        .filter((item) => Boolean(item?.objectName?.trim()) && item.enabled !== false)
        .map((item) => this.getKnownObjectName(item.objectName) ?? item.objectName)
    );

    const candidates = new Map<string, ISalesforceRelatedEntityCandidate>();
    const state = this.relatedEntityState.get(normalizedObjectName);
    (state?.relationOptions ?? [])
      .filter((option) => this.isRelationOption(option))
      .forEach((option) => {
        const targetObject = this.getRelationTargetObject(option);
        if (!targetObject || targetObject === normalizedObjectName || !enabledObjects.has(targetObject)) {
          return;
        }

        const existing = candidates.get(targetObject);
        if (existing) {
          if (!existing.relationSegments.some((s) => s.segment === option.segment)) {
            existing.relationSegments.push({ segment: option.segment, label: option.label });
            existing.relationSegments.sort((a, b) => a.segment.localeCompare(b.segment));
          }
          return;
        }

        candidates.set(targetObject, {
          objectName: targetObject,
          relationSegments: [{ segment: option.segment, label: option.label }],
        });
      });

    return Array.from(candidates.values()).sort((a, b) =>
      this.getObjectDisplay(a.objectName).localeCompare(this.getObjectDisplay(b.objectName))
    );
  }

  getPossibleRelatedEntitiesLoading(mappingIndex: number): boolean {
    const mapping = this._config.objects[mappingIndex];
    const objectName = mapping?.objectName?.trim();
    if (!objectName) {
      return false;
    }
    const normalizedObjectName = this.getKnownObjectName(objectName) ?? objectName;
    this.ensureRelatedEntityCandidatesLoaded(normalizedObjectName);
    return Boolean(this.relatedEntityState.get(normalizedObjectName)?.loading);
  }

  getPossibleRelatedEntitiesError(mappingIndex: number): string | null {
    const mapping = this._config.objects[mappingIndex];
    const objectName = mapping?.objectName?.trim();
    if (!objectName) {
      return null;
    }
    const normalizedObjectName = this.getKnownObjectName(objectName) ?? objectName;
    this.ensureRelatedEntityCandidatesLoaded(normalizedObjectName);
    return this.relatedEntityState.get(normalizedObjectName)?.error ?? null;
  }

  getRelatedEntityDisplay(candidate: ISalesforceRelatedEntityCandidate): string {
    return this.getObjectDisplay(candidate.objectName) || candidate.objectName;
  }

  getRelatedEntityDetail(candidate: ISalesforceRelatedEntityCandidate): string {
    return candidate.relationSegments.length === 1 ? '1 direct relation' : `${candidate.relationSegments.length} direct relations`;
  }

  selectRelatedEntityPath(mappingIndex: number, objectName: string, relationSegment: string): void {
    const key = this.getRelatedEntitySelectionKey(mappingIndex, objectName);
    const isDeselecting = this.selectedRelatedEntityPaths.get(key) === relationSegment;

    if (isDeselecting) {
      this.selectedRelatedEntityPaths.delete(key);
    } else {
      this.selectedRelatedEntityPaths.set(key, relationSegment);
    }

    const mapping = this._config.objects[mappingIndex];
    if (mapping) {
      const relatedObjects: ISalesforceRelatedObject[] = (mapping.relatedObjects ?? []).filter((r) => r.objectName !== objectName);
      if (!isDeselecting) {
        relatedObjects.push({ objectName, relationSegment });
      }
      mapping.relatedObjects = relatedObjects;
      this.emitChange();
    }

    this.cdr.markForCheck();
  }

  isRelatedEntityPathSelected(mappingIndex: number, objectName: string, relationSegment: string): boolean {
    const key = this.getRelatedEntitySelectionKey(mappingIndex, objectName);
    return this.selectedRelatedEntityPaths.get(key) === relationSegment;
  }

  getPathBuilderState(mappingIndex: number, fieldIndex: number): ISalesforcePathBuilderState {
    const key = this.getPathBuilderKey(mappingIndex, fieldIndex);
    const existing = this.pathBuilderState.get(key);
    if (existing) {
      return existing;
    }

    const state: ISalesforcePathBuilderState = {
      selections: [null],
      optionsByLevel: [[]],
      objectNameByLevel: [this._config.objects[mappingIndex]?.objectName ?? ''],
      loadingByLevel: [false],
      errorsByLevel: [null],
      searchByLevel: [''],
      openByLevel: [false],
      flyoutColumnsByLevel: [[]],
      flyoutCanScrollLeftByLevel: [false],
      flyoutCanScrollRightByLevel: [false],
      invalidSavedPath: false,
    };
    this.pathBuilderState.set(key, state);
    this.hydratePathBuilderFromFieldPath(mappingIndex, fieldIndex);
    return state;
  }

  getPathLevelLoading(mappingIndex: number, fieldIndex: number, level: number): boolean {
    return Boolean(this.getPathBuilderState(mappingIndex, fieldIndex).loadingByLevel[level]);
  }

  getPathLevelError(mappingIndex: number, fieldIndex: number, level: number): string | null {
    return this.getPathBuilderState(mappingIndex, fieldIndex).errorsByLevel[level] ?? null;
  }

  getSelectedPathSegments(mappingIndex: number, fieldIndex: number): string[] {
    return this.getPathBuilderState(mappingIndex, fieldIndex).selections
      .filter((option): option is ISalesforcePathOption => Boolean(option))
      .map((option) => this.formatPathOption(option));
  }

  getPathLevelSearchTerm(mappingIndex: number, fieldIndex: number, level: number): string {
    return this.getPathBuilderState(mappingIndex, fieldIndex).searchByLevel[level] ?? '';
  }

  onPathLevelSearchChange(mappingIndex: number, fieldIndex: number, level: number, term: string): void {
    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    state.searchByLevel[level] = term ?? '';
    state.openByLevel[level] = true;
    this.initFlyoutColumns(mappingIndex, fieldIndex, level);
  }

  setPathLevelOpen(mappingIndex: number, fieldIndex: number, level: number, open: boolean): void {
    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    state.openByLevel[level] = open;
    if (open) {
      this.initFlyoutColumns(mappingIndex, fieldIndex, level);
      this.updateFlyoutScrollStateAsync(mappingIndex, fieldIndex, level);
    } else {
      state.flyoutColumnsByLevel[level] = [];
      state.flyoutCanScrollLeftByLevel[level] = false;
      state.flyoutCanScrollRightByLevel[level] = false;
    }
  }

  cancelPathLevelClose(mappingIndex: number, fieldIndex: number, level: number): void {
    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    state.openByLevel[level] = true;
  }

  closePathLevel(mappingIndex: number, fieldIndex: number, level: number): void {
    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    state.openByLevel[level] = false;
    state.flyoutColumnsByLevel[level] = [];
    state.flyoutCanScrollLeftByLevel[level] = false;
    state.flyoutCanScrollRightByLevel[level] = false;
  }

  isPathLevelOpen(mappingIndex: number, fieldIndex: number, level: number): boolean {
    return Boolean(this.getPathBuilderState(mappingIndex, fieldIndex).openByLevel[level]);
  }

  getPathLevelDomKey(mappingIndex: number, fieldIndex: number, level: number): string {
    return `${mappingIndex}-${fieldIndex}-${level}`;
  }

  getSelectedPathLevelLabel(mappingIndex: number, fieldIndex: number, level: number): string {
    const selected = this.getPathBuilderState(mappingIndex, fieldIndex).selections[level];
    return selected ? this.formatPathOption(selected) : '';
  }

  getFlyoutColumns(mappingIndex: number, fieldIndex: number, level: number): ISalesforceFlyoutColumn[] {
    return this.getPathBuilderState(mappingIndex, fieldIndex).flyoutColumnsByLevel[level] ?? [];
  }

  getFlyoutBreadcrumb(mappingIndex: number, fieldIndex: number, level: number): string {
    const columns = this.getFlyoutColumns(mappingIndex, fieldIndex, level);
    const objectChain = columns
      .map((column) => this.getObjectDisplay(column.targetObject) || column.targetObject)
      .filter((name) => Boolean(name?.trim()));
    return objectChain.join(' > ');
  }

  trackByPathOption(index: number, option: ISalesforcePathOption): string {
    return `${option.associatedEntity ?? ''}:${option.segment ?? ''}:${option.type ?? ''}`;
  }

  trackByFlyoutColumn(index: number, column: ISalesforceFlyoutColumn): string {
    return `${column.targetObject}:${index}`;
  }

  canFlyoutScrollLeft(mappingIndex: number, fieldIndex: number, level: number): boolean {
    return Boolean(this.getPathBuilderState(mappingIndex, fieldIndex).flyoutCanScrollLeftByLevel[level]);
  }

  canFlyoutScrollRight(mappingIndex: number, fieldIndex: number, level: number): boolean {
    return Boolean(this.getPathBuilderState(mappingIndex, fieldIndex).flyoutCanScrollRightByLevel[level]);
  }

  getFlyoutDropdownWidth(mappingIndex: number, fieldIndex: number, level: number): number {
    const columnCount = Math.max(1, this.getFlyoutColumns(mappingIndex, fieldIndex, level).length);
    const perColumnWidth = 280;
    const horizontalPadding = 8;
    const minWidth = 300;
    const maxWidth = 840;
    return Math.min(maxWidth, Math.max(minWidth, columnCount * perColumnWidth + horizontalPadding));
  }

  onFlyoutScroll(mappingIndex: number, fieldIndex: number, level: number): void {
    this.updateFlyoutScrollState(mappingIndex, fieldIndex, level);
  }

  scrollFlyoutColumns(mappingIndex: number, fieldIndex: number, level: number, direction: 'left' | 'right'): void {
    const dropdown = this.getPathDropdownElement(mappingIndex, fieldIndex, level);
    if (!dropdown) {
      return;
    }
    const offset = direction === 'left' ? -280 : 280;
    dropdown.scrollBy({ left: offset, behavior: 'smooth' });
    setTimeout(() => this.updateFlyoutScrollState(mappingIndex, fieldIndex, level), 180);
  }

  isRelationOptionSelectedForColumn(
    mappingIndex: number,
    fieldIndex: number,
    level: number,
    columnIndex: number,
    option: ISalesforcePathOption
  ): boolean {
    const columns = this.getFlyoutColumns(mappingIndex, fieldIndex, level);
    const selected = columns[columnIndex]?.selectedRelation;
    if (!selected) {
      return false;
    }
    return selected.segment === option.segment && selected.associatedEntity === option.associatedEntity;
  }

  onFlyoutColumnSearchChange(mappingIndex: number, fieldIndex: number, level: number, columnIndex: number, term: string): void {
    const columns = this.getFlyoutColumns(mappingIndex, fieldIndex, level);
    if (!columns[columnIndex]) {
      return;
    }
    columns[columnIndex].searchTerm = term ?? '';
  }

  getFilteredFlyoutColumnOptions(
    mappingIndex: number,
    fieldIndex: number,
    level: number,
    columnIndex: number
  ): ISalesforcePathOption[] {
    const columns = this.getFlyoutColumns(mappingIndex, fieldIndex, level);
    const column = columns[columnIndex];
    if (!column) {
      return [];
    }

    const cappedOptions = column.options.filter((option) => {
      if (this.isRelationOption(option) && this.isAtAssociatedEntityDepthCap(mappingIndex, fieldIndex, level, columnIndex)) {
        return false;
      }
      return true;
    });

    const term = (column.searchTerm ?? '').trim().toLowerCase();
    if (!term) {
      return cappedOptions;
    }

    return cappedOptions.filter((option) => {
      const segment = option.segment?.toLowerCase() ?? '';
      const label = option.label?.toLowerCase() ?? '';
      const display = this.formatPathOption(option).toLowerCase();
      return segment.includes(term) || label.includes(term) || display.includes(term);
    });
  }

  hasSelectableFlyoutOptions(mappingIndex: number, fieldIndex: number, level: number, columnIndex: number): boolean {
    return this.getFilteredFlyoutColumnOptions(mappingIndex, fieldIndex, level, columnIndex).length > 0;
  }

  isAtAssociatedEntityDepthCap(mappingIndex: number, fieldIndex: number, level: number, columnIndex: number): boolean {
    return this.getProposedRelationDepth(mappingIndex, fieldIndex, level, columnIndex) > this.maxAssociatedEntityDepth;
  }

  onFlyoutOptionHover(
    mappingIndex: number,
    fieldIndex: number,
    level: number,
    columnIndex: number,
    option: ISalesforcePathOption
  ): void {
    this.cancelPathLevelClose(mappingIndex, fieldIndex, level);
    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    const columns = state.flyoutColumnsByLevel[level] ?? [];
    if (!columns[columnIndex]) {
      return;
    }

    const previousSelected = columns[columnIndex].selectedRelation;
    columns[columnIndex].selectedRelation = this.isRelationOption(option) ? option : null;
    if (!this.isRelationOption(option)) {
      state.flyoutColumnsByLevel[level] = columns.slice(0, columnIndex + 1);
      return;
    }

    if (this.isAtAssociatedEntityDepthCap(mappingIndex, fieldIndex, level, columnIndex)) {
      return;
    }

    const targetObject = this.getRelationTargetObject(option);
    if (!targetObject) {
      return;
    }

    const existingNextColumn = columns[columnIndex + 1];
    if (
      previousSelected &&
      previousSelected.segment === option.segment &&
      previousSelected.associatedEntity === option.associatedEntity &&
      existingNextColumn?.targetObject === targetObject
    ) {
      return;
    }

    state.flyoutColumnsByLevel[level] = columns.slice(0, columnIndex + 1);

    const nextColumn: ISalesforceFlyoutColumn = {
      targetObject,
      options: [],
      loading: true,
      error: null,
      searchTerm: '',
      selectedRelation: null,
    };
    state.flyoutColumnsByLevel[level].push(nextColumn);
    this.scrollOpenFlyoutToEnd();
    this.updateFlyoutScrollStateAsync(mappingIndex, fieldIndex, level);

    this.loadOptionsForObject(
      targetObject,
      (options) => {
        const currentState = this.getPathBuilderState(mappingIndex, fieldIndex);
        const currentColumns = currentState.flyoutColumnsByLevel[level] ?? [];
        const currentNextColumn = currentColumns[columnIndex + 1];
        if (!currentNextColumn || currentNextColumn.targetObject !== targetObject) {
          return;
        }

        currentNextColumn.options = options;
        currentNextColumn.loading = false;
        currentNextColumn.error = null;
        this.scrollOpenFlyoutToEnd();
        this.updateFlyoutScrollStateAsync(mappingIndex, fieldIndex, level);
        this.cdr.markForCheck();
      },
      () => {
        const currentState = this.getPathBuilderState(mappingIndex, fieldIndex);
        const currentColumns = currentState.flyoutColumnsByLevel[level] ?? [];
        const currentNextColumn = currentColumns[columnIndex + 1];
        if (!currentNextColumn || currentNextColumn.targetObject !== targetObject) {
          return;
        }

        currentNextColumn.options = [];
        currentNextColumn.loading = false;
        currentNextColumn.error = 'Unable to load related fields';
        this.scrollOpenFlyoutToEnd();
        this.updateFlyoutScrollStateAsync(mappingIndex, fieldIndex, level);
        this.cdr.markForCheck();
      }
    );
  }

  onFlyoutOptionSelect(
    mappingIndex: number,
    fieldIndex: number,
    level: number,
    columnIndex: number,
    option: ISalesforcePathOption
  ): void {
    if (this.isRelationOption(option)) {
      this.cancelPathLevelClose(mappingIndex, fieldIndex, level);
      this.onFlyoutOptionHover(mappingIndex, fieldIndex, level, columnIndex, option);
      const state = this.getPathBuilderState(mappingIndex, fieldIndex);
      state.openByLevel[level] = true;
      return;
    }

    const columns = this.getFlyoutColumns(mappingIndex, fieldIndex, level);
    const selectedChain: ISalesforcePathOption[] = [];
    for (let index = 0; index < columnIndex; index += 1) {
      const relation = columns[index]?.selectedRelation;
      if (!relation) {
        return;
      }
      selectedChain.push(relation);
    }
    selectedChain.push(option);

    this.commitFlyoutPath(mappingIndex, fieldIndex, level, selectedChain);
    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    state.searchByLevel[level] = '';
    state.openByLevel[level] = false;
    state.flyoutColumnsByLevel[level] = [];
    state.flyoutCanScrollLeftByLevel[level] = false;
    state.flyoutCanScrollRightByLevel[level] = false;
  }

  getPathLevels(mappingIndex: number, fieldIndex: number): number[] {
    const count = this.getPathBuilderState(mappingIndex, fieldIndex).selections.length;
    return Array.from({ length: count }, (_, index) => index);
  }

  isBuiltPathInvalid(mappingIndex: number, fieldIndex: number): boolean {
    return Boolean(this.getPathBuilderState(mappingIndex, fieldIndex).invalidSavedPath);
  }

  getPathLevelLabel(mappingIndex: number, fieldIndex: number, level: number): string {
    const objectName = this.getPathBuilderState(mappingIndex, fieldIndex).objectNameByLevel[level];
    if (!objectName?.trim()) {
      return `Level ${level + 1}`;
    }
    return `From ${objectName}`;
  }

  onPathLevelChange(mappingIndex: number, fieldIndex: number, level: number, selectedSegment: string): void {
    const mapping = this._config.objects[mappingIndex];
    const field = mapping?.fields?.[fieldIndex];
    if (!mapping || !field) {
      return;
    }

    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    const options = state.optionsByLevel[level] ?? [];
    const selected = options.find((option) => option.segment === selectedSegment) ?? null;

    state.selections[level] = selected;
    state.selections = state.selections.slice(0, level + 1);
    state.optionsByLevel = state.optionsByLevel.slice(0, level + 1);
    state.objectNameByLevel = state.objectNameByLevel.slice(0, level + 1);
    state.loadingByLevel = state.loadingByLevel.slice(0, level + 1);
    state.errorsByLevel = state.errorsByLevel.slice(0, level + 1);
    state.searchByLevel = state.searchByLevel.slice(0, level + 1);
    state.openByLevel = state.openByLevel.slice(0, level + 1);
    state.flyoutColumnsByLevel = state.flyoutColumnsByLevel.slice(0, level + 1);
    state.flyoutCanScrollLeftByLevel = state.flyoutCanScrollLeftByLevel.slice(0, level + 1);
    state.flyoutCanScrollRightByLevel = state.flyoutCanScrollRightByLevel.slice(0, level + 1);
    state.invalidSavedPath = false;

    if (this.isRelationOption(selected)) {
      state.selections.push(null);
      state.optionsByLevel.push([]);
      state.objectNameByLevel.push('');
      state.loadingByLevel.push(false);
      state.errorsByLevel.push(null);
      state.searchByLevel.push('');
      state.openByLevel.push(false);
      state.flyoutColumnsByLevel.push([]);
      state.flyoutCanScrollLeftByLevel.push(false);
      state.flyoutCanScrollRightByLevel.push(false);

      const relationTarget = this.getRelationTargetObject(selected);
      if (relationTarget) {
        this.ensurePathOptions(mappingIndex, fieldIndex, level + 1, relationTarget);
      } else {
        state.loadingByLevel[level + 1] = false;
        state.errorsByLevel[level + 1] = 'Unable to resolve related object';
      }
    }

    field.path = state.selections
      .filter((option): option is ISalesforcePathOption => Boolean(option))
      .map((option) => option.segment)
      .join('.');

    if (selected) {
      field.label = selected.label?.trim() || '';
    } else {
      field.label = '';
    }

    if (selected && !this.isRelationOption(selected)) {
      field.dataType = this.mapSalesforceTypeToFieldDataType(selected.sourceType ?? selected.type, selected.segment);
    }

    this.onModelChange();
  }

  canStepBackPath(mappingIndex: number, fieldIndex: number): boolean {
    const selectedCount = this.getPathBuilderState(mappingIndex, fieldIndex).selections.filter((option) => Boolean(option)).length;
    return selectedCount > 0;
  }

  stepBackPath(mappingIndex: number, fieldIndex: number): void {
    const mapping = this._config.objects[mappingIndex];
    const field = mapping?.fields?.[fieldIndex];
    if (!mapping || !field) {
      return;
    }

    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    let lastSelectedLevel = -1;
    state.selections.forEach((option, index) => {
      if (option) {
        lastSelectedLevel = index;
      }
    });

    if (lastSelectedLevel < 0) {
      return;
    }

    state.selections = state.selections.slice(0, lastSelectedLevel + 1);
    state.optionsByLevel = state.optionsByLevel.slice(0, lastSelectedLevel + 1);
    state.objectNameByLevel = state.objectNameByLevel.slice(0, lastSelectedLevel + 1);
    state.loadingByLevel = state.loadingByLevel.slice(0, lastSelectedLevel + 1);
    state.errorsByLevel = state.errorsByLevel.slice(0, lastSelectedLevel + 1);
    state.searchByLevel = state.searchByLevel.slice(0, lastSelectedLevel + 1);
    state.openByLevel = state.openByLevel.slice(0, lastSelectedLevel + 1);
    state.flyoutColumnsByLevel = state.flyoutColumnsByLevel.slice(0, lastSelectedLevel + 1);
    state.flyoutCanScrollLeftByLevel = state.flyoutCanScrollLeftByLevel.slice(0, lastSelectedLevel + 1);
    state.flyoutCanScrollRightByLevel = state.flyoutCanScrollRightByLevel.slice(0, lastSelectedLevel + 1);
    state.selections[lastSelectedLevel] = null;
    state.searchByLevel[lastSelectedLevel] = '';
    state.flyoutColumnsByLevel[lastSelectedLevel] = [];
    state.flyoutCanScrollLeftByLevel[lastSelectedLevel] = false;
    state.flyoutCanScrollRightByLevel[lastSelectedLevel] = false;
    state.invalidSavedPath = false;

    field.path = state.selections
      .filter((option): option is ISalesforcePathOption => Boolean(option))
      .map((option) => option.segment)
      .join('.');
    const labelOption = state.selections
      .filter((option): option is ISalesforcePathOption => Boolean(option))
      .slice(-1)[0];
    field.label = labelOption?.label?.trim() || '';

    this.onModelChange();
  }

  clearPathPicker(mappingIndex: number, fieldIndex: number): void {
    const mapping = this._config.objects[mappingIndex];
    const field = mapping?.fields?.[fieldIndex];
    if (!mapping || !field) {
      return;
    }

    this.pathBuilderState.delete(this.getPathBuilderKey(mappingIndex, fieldIndex));
    field.path = '';
    field.label = '';
    this.onModelChange();
  }

  formatPathOption(option?: ISalesforcePathOption): string {
    if (!option) {
      return '';
    }
    const base = option.label === option.segment ? option.segment : `${option.label} (${option.segment})`;
    return this.isRelationOption(option) ? `${base} [relation]` : base;
  }

  private initFlyoutColumns(mappingIndex: number, fieldIndex: number, level: number): void {
    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    const targetObject = state.objectNameByLevel[level] || this._config.objects[mappingIndex]?.objectName || '';
    state.flyoutColumnsByLevel[level] = [
      {
        targetObject,
        options: state.optionsByLevel[level] ?? [],
        loading: Boolean(state.loadingByLevel[level]),
        error: state.errorsByLevel[level] ?? null,
        searchTerm: state.searchByLevel[level] ?? '',
        selectedRelation: null,
      },
    ];
    this.updateFlyoutScrollStateAsync(mappingIndex, fieldIndex, level);
  }

  private commitFlyoutPath(
    mappingIndex: number,
    fieldIndex: number,
    level: number,
    selectedChain: ISalesforcePathOption[]
  ): void {
    const mapping = this._config.objects[mappingIndex];
    const field = mapping?.fields?.[fieldIndex];
    if (!mapping || !field || !selectedChain.length) {
      return;
    }

    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    const flyoutColumns = state.flyoutColumnsByLevel[level] ?? [];
    const preservedSelections = state.selections
      .slice(0, level)
      .filter((option): option is ISalesforcePathOption => Boolean(option));

    state.selections = [...preservedSelections];
    state.optionsByLevel = state.optionsByLevel.slice(0, level);
    state.objectNameByLevel = state.objectNameByLevel.slice(0, level);
    state.loadingByLevel = state.loadingByLevel.slice(0, level);
    state.errorsByLevel = state.errorsByLevel.slice(0, level);
    state.searchByLevel = state.searchByLevel.slice(0, level);
    state.openByLevel = state.openByLevel.slice(0, level);
    state.flyoutColumnsByLevel = state.flyoutColumnsByLevel.slice(0, level);
    state.flyoutCanScrollLeftByLevel = state.flyoutCanScrollLeftByLevel.slice(0, level);
    state.flyoutCanScrollRightByLevel = state.flyoutCanScrollRightByLevel.slice(0, level);

    selectedChain.forEach((option, index) => {
      const column = flyoutColumns[index];
      state.selections.push(option);
      state.optionsByLevel.push(column?.options ?? []);
      state.objectNameByLevel.push(column?.targetObject ?? '');
      state.loadingByLevel.push(false);
      state.errorsByLevel.push(null);
      state.searchByLevel.push('');
      state.openByLevel.push(false);
      state.flyoutColumnsByLevel.push([]);
      state.flyoutCanScrollLeftByLevel.push(false);
      state.flyoutCanScrollRightByLevel.push(false);
    });

    field.path = state.selections
      .filter((option): option is ISalesforcePathOption => Boolean(option))
      .map((option) => option.segment)
      .join('.');

    const lastOption = selectedChain[selectedChain.length - 1];
    if (lastOption) {
      field.label = lastOption.label?.trim() || '';
    } else {
      field.label = '';
    }
    if (lastOption && !this.isRelationOption(lastOption)) {
      field.dataType = this.mapSalesforceTypeToFieldDataType(lastOption.sourceType ?? lastOption.type, lastOption.segment);
    }

    this.onModelChange();
  }

  private emitChange(): void {
    this.configChange.emit(this.cloneConfig(this._config));
  }

  private hydratePathBuilderFromFieldPath(mappingIndex: number, fieldIndex: number): void {
    const key = this.getPathBuilderKey(mappingIndex, fieldIndex);
    if (this.hydratingPathBuilderKeys.has(key)) {
      return;
    }

    const mapping = this._config.objects[mappingIndex];
    const field = mapping?.fields?.[fieldIndex];
    if (!mapping || !field) {
      return;
    }

    const objectName = mapping.objectName?.trim();
    if (!objectName) {
      return;
    }

    const rawPath = field.path?.trim() ?? '';
    if (!rawPath) {
      this.ensurePathOptions(mappingIndex, fieldIndex, 0, objectName);
      return;
    }

    const segments = rawPath
      .split('.')
      .map((segment) => segment.trim())
      .filter(Boolean);
    if (!segments.length) {
      this.ensurePathOptions(mappingIndex, fieldIndex, 0, objectName);
      return;
    }

    const state = this.pathBuilderState.get(key);
    if (!state) {
      return;
    }

    this.hydratingPathBuilderKeys.add(key);
    const selectedOptions: ISalesforcePathOption[] = [];
    const loadedOptionsByLevel: ISalesforcePathOption[][] = [];
    const loadedObjectNamesByLevel: string[] = [];

    const markInvalidAndResetBuilder = () => {
      this.hydratingPathBuilderKeys.delete(key);
      this.resetBuilderState(mappingIndex, fieldIndex);
      const currentState = this.pathBuilderState.get(key);
      if (currentState) {
        currentState.invalidSavedPath = true;
      }
      this.updateValidity();
      this.cdr.markForCheck();
    };

    const finalize = () => {
      const currentState = this.pathBuilderState.get(key);
      if (!currentState) {
        this.hydratingPathBuilderKeys.delete(key);
        return;
      }

      currentState.selections = [...selectedOptions];
      currentState.optionsByLevel = [...loadedOptionsByLevel];
      currentState.objectNameByLevel = [...loadedObjectNamesByLevel];
      currentState.loadingByLevel = currentState.selections.map(() => false);
      currentState.errorsByLevel = currentState.selections.map(() => null);
      currentState.searchByLevel = currentState.selections.map(() => '');
      currentState.openByLevel = currentState.selections.map(() => false);
      currentState.flyoutColumnsByLevel = currentState.selections.map(() => []);
      currentState.flyoutCanScrollLeftByLevel = currentState.selections.map(() => false);
      currentState.flyoutCanScrollRightByLevel = currentState.selections.map(() => false);
      currentState.invalidSavedPath = false;

      const correctedPath = selectedOptions.map((o) => o.segment).join('.');
      if (correctedPath && correctedPath !== rawPath) {
        field.path = correctedPath;
      }

      const lastSelected = currentState.selections[currentState.selections.length - 1];
      if (lastSelected) {
        field.label = lastSelected.label?.trim() || '';
      } else {
        field.label = '';
      }
      if (lastSelected && !this.isRelationOption(lastSelected)) {
        field.dataType = this.mapSalesforceTypeToFieldDataType(lastSelected.sourceType ?? lastSelected.type, lastSelected.segment);
      }

      this.hydratingPathBuilderKeys.delete(key);
      this.updateValidity();
      this.cdr.markForCheck();
    };

    const resolveLevel = (level: number, currentObject: string): void => {
      this.loadOptionsForObject(
        currentObject,
        (options) => {
          const currentState = this.pathBuilderState.get(key);
          if (!currentState) {
            this.hydratingPathBuilderKeys.delete(key);
            return;
          }

          loadedOptionsByLevel[level] = options;
          loadedObjectNamesByLevel[level] = currentObject;

          const segment = segments[level];
          const matched = this.findPathOptionForSegment(options, segment);
          if (!matched) {
            markInvalidAndResetBuilder();
            return;
          }

          selectedOptions[level] = matched;
          const isLastSegment = level === segments.length - 1;
          if (isLastSegment) {
            finalize();
            return;
          }

          if (!this.isRelationOption(matched)) {
            markInvalidAndResetBuilder();
            return;
          }

          const relationTarget = this.getRelationTargetObject(matched);
          if (!relationTarget) {
            markInvalidAndResetBuilder();
            return;
          }

          resolveLevel(level + 1, relationTarget);
        },
        () => markInvalidAndResetBuilder()
      );
    };

    resolveLevel(0, objectName);
  }

  private resetBuilderState(mappingIndex: number, fieldIndex: number): void {
    const key = this.getPathBuilderKey(mappingIndex, fieldIndex);
    const state = this.pathBuilderState.get(key);
    const objectName = this._config.objects[mappingIndex]?.objectName ?? '';
    if (!state) {
      return;
    }

    state.selections = [null];
    state.optionsByLevel = [[]];
    state.objectNameByLevel = [objectName];
    state.loadingByLevel = [false];
    state.errorsByLevel = [null];
    state.searchByLevel = [''];
    state.openByLevel = [false];
    state.flyoutColumnsByLevel = [[]];
    state.flyoutCanScrollLeftByLevel = [false];
    state.flyoutCanScrollRightByLevel = [false];
    state.invalidSavedPath = false;
    this.ensurePathOptions(mappingIndex, fieldIndex, 0, objectName);
  }

  private findPathOptionForSegment(options: ISalesforcePathOption[], segment: string): ISalesforcePathOption | undefined {
    const raw = segment?.trim();
    if (!raw) {
      return undefined;
    }

    const direct = options.find((option) => option.segment === raw);
    if (direct) {
      return direct;
    }

    const lowerRaw = raw.toLowerCase();
    const caseInsensitive = options.find((option) => (option.segment ?? '').toLowerCase() === lowerRaw);
    if (caseInsensitive) {
      return caseInsensitive;
    }

    if (raw.endsWith('__c')) {
      const relationVariant = `${raw.slice(0, -3)}__r`;
      const match = options.find((option) => option.segment === relationVariant);
      if (match) {
        return match;
      }
    } else if (raw.endsWith('__r')) {
      const idVariant = `${raw.slice(0, -3)}__c`;
      const match = options.find((option) => option.segment === idVariant);
      if (match) {
        return match;
      }
    }

    if (raw.endsWith('Id')) {
      const relationVariant = raw.slice(0, -2);
      const match = options.find((option) => option.segment === relationVariant);
      if (match) {
        return match;
      }
    } else {
      const idVariant = `${raw}Id`;
      const match = options.find((option) => option.segment === idVariant);
      if (match) {
        return match;
      }
    }

    return undefined;
  }

  private getRelationIdSegment(relationSegment: string): string | undefined {
    const segment = relationSegment?.trim();
    if (!segment) {
      return undefined;
    }
    if (segment.endsWith('__r')) {
      return `${segment.slice(0, -3)}__c`;
    }
    if (segment.endsWith('Id')) {
      return segment;
    }
    return `${segment}Id`;
  }

  private getRelationIdLabel(field: ISalesforceObjectMetaField, relationSegment: string): string {
    const baseLabel = field.label?.trim() || relationSegment;
    if (baseLabel.toLowerCase().endsWith(' id')) {
      return baseLabel;
    }
    return `${baseLabel} Id`;
  }

  private ensurePathOptions(mappingIndex: number, fieldIndex: number, level: number, objectName?: string): void {
    if (!objectName?.trim()) {
      return;
    }

    const builderKey = this.getPathBuilderKey(mappingIndex, fieldIndex);
    const state = this.pathBuilderState.get(builderKey);
    if (!state) {
      return;
    }

    this.loadOptionsForObject(
      objectName,
      (options) => {
        const currentState = this.pathBuilderState.get(builderKey);
        if (!currentState) {
          return;
        }

        currentState.optionsByLevel[level] = options;
        currentState.objectNameByLevel[level] = objectName;
        currentState.loadingByLevel[level] = false;
        currentState.errorsByLevel[level] = null;
        this.cdr.markForCheck();
      },
      () => {
        const currentState = this.pathBuilderState.get(builderKey);
        if (!currentState) {
          return;
        }

        currentState.optionsByLevel[level] = [];
        currentState.objectNameByLevel[level] = objectName;
        currentState.loadingByLevel[level] = false;
        currentState.errorsByLevel[level] = 'Unable to load fields';
        this.cdr.markForCheck();
      },
      () => {
        const currentState = this.pathBuilderState.get(builderKey);
        if (!currentState) {
          return;
        }
        currentState.loadingByLevel[level] = true;
        currentState.errorsByLevel[level] = null;
      }
    );
  }

  private mapFieldOptions(objectName: string, fields: ISalesforceObjectMetaField[]): ISalesforcePathOption[] {
    const prefix = `${objectName}.`;
    const dedup = new Map<string, ISalesforcePathOption>();

    (fields ?? []).forEach((field) => {
      const rawPath = field?.path?.trim();
      if (!rawPath || field?.type === 'to-many') {
        return;
      }

      const relativePath = rawPath.startsWith(prefix) ? rawPath.substring(prefix.length) : rawPath;
      if (!relativePath || relativePath.includes('.')) {
        return;
      }

      const segment = relativePath;
      const isRelationCandidate = this.isRelationField(field, segment);
      const relationTarget = isRelationCandidate ? this.getAssociatedEntity(field, segment) : undefined;
      const isRelation = Boolean(isRelationCandidate && relationTarget);

      dedup.set(segment, {
        segment,
        label: field.label?.trim() || segment,
        type: isRelation ? 'to-one' : this.getNonRelationOptionType(field.type),
        sourceType: field.type,
        associatedEntity: isRelation ? relationTarget : undefined,
      });

      // Add relationship-id segment companion (e.g. TR1__Contact__c for TR1__Contact__r).
      if (isRelation) {
        const relationIdSegment = this.getRelationIdSegment(segment);
        if (relationIdSegment && !dedup.has(relationIdSegment)) {
          dedup.set(relationIdSegment, {
            segment: relationIdSegment,
            label: this.getRelationIdLabel(field, segment),
            type: 'id',
            sourceType: 'id',
          });
        }
      }
    });

    return Array.from(dedup.values()).sort((a, b) => a.label.localeCompare(b.label));
  }

  private isRelationOption(option?: ISalesforcePathOption | null): boolean {
    if (!option) {
      return false;
    }
    return option.type === 'to-one';
  }

  private isRelationField(field: ISalesforceObjectMetaField, segment: string): boolean {
    const type = field.type?.trim().toLowerCase();
    if (type === 'to-one') {
      return true;
    }
    if (type === 'reference' || type === 'lookup' || type === 'master-detail') {
      return true;
    }
    if (field.associatedEntity?.trim()) {
      return true;
    }
    if (field.relationshipName?.trim()) {
      return true;
    }
    return segment.endsWith('__r');
  }

  private getAssociatedEntity(field: ISalesforceObjectMetaField, segment: string): string | undefined {
    const explicitAssociatedEntity = field.associatedEntity?.trim();
    if (explicitAssociatedEntity) {
      return this.getKnownObjectName(explicitAssociatedEntity) ?? explicitAssociatedEntity;
    }
    return undefined;
  }

  private getRelationTargetObject(option: ISalesforcePathOption): string | undefined {
    const explicit = option.associatedEntity?.trim();
    if (explicit) {
      return explicit;
    }
    return undefined;
  }

  private getNonRelationOptionType(type: string | undefined): string | undefined {
    const normalizedType = type?.trim().toLowerCase();
    if (normalizedType === 'to-one') {
      return 'reference';
    }
    return type;
  }

  private loadOptionsForObject(
    objectName: string,
    onSuccess: (options: ISalesforcePathOption[]) => void,
    onError: () => void,
    onStart?: () => void
  ): void {
    const normalizedObjectName = this.getKnownObjectName(objectName) ?? objectName;
    if (this.fieldOptionsCache.has(normalizedObjectName)) {
      onSuccess(this.fieldOptionsCache.get(normalizedObjectName) ?? []);
      return;
    }

    if (onStart) {
      onStart();
    }

    let request$ = this.fieldOptionsRequests.get(normalizedObjectName);
    if (!request$) {
      request$ = this.metadataService.getObjectMeta(normalizedObjectName).pipe(
        map((meta) => this.mapFieldOptions(normalizedObjectName, meta?.fields ?? [])),
        shareReplay(1)
      );
      this.fieldOptionsRequests.set(normalizedObjectName, request$);
    }

    const sub = request$.subscribe({
      next: (options) => {
        this.fieldOptionsCache.set(normalizedObjectName, options);
        onSuccess(options);
      },
      error: () => {
        this.fieldOptionsRequests.delete(normalizedObjectName);
        onError();
      },
    });
    this.subscriptions.add(sub);
  }

  private preloadRelatedEntityCandidates(): void {
    this._config.objects
      .map((mapping) => mapping?.objectName?.trim())
      .filter((objectName): objectName is string => Boolean(objectName))
      .forEach((objectName) => this.ensureRelatedEntityCandidatesLoaded(objectName));
  }

  private ensureRelatedEntityCandidatesLoaded(objectName: string): void {
    const normalizedObjectName = this.getKnownObjectName(objectName) ?? objectName;
    const existingState = this.relatedEntityState.get(normalizedObjectName);
    if (existingState?.loading || existingState?.relationOptions.length) {
      return;
    }

    this.relatedEntityState.set(normalizedObjectName, {
      loading: true,
      error: null,
      relationOptions: existingState?.relationOptions ?? [],
    });

    this.loadOptionsForObject(
      normalizedObjectName,
      (options) => {
        this.relatedEntityState.set(normalizedObjectName, {
          loading: false,
          error: null,
          relationOptions: options.filter((option) => this.isRelationOption(option)),
        });
        this.cdr.markForCheck();
      },
      () => {
        this.relatedEntityState.set(normalizedObjectName, {
          loading: false,
          error: 'Unable to load related entities',
          relationOptions: [],
        });
        this.cdr.markForCheck();
      }
    );
  }

  private getRelatedEntitySelectionKey(mappingIndex: number, objectName: string): string {
    return `${mappingIndex}:${objectName}`;
  }

  private hydrateRelatedEntitySelections(): void {
    this.selectedRelatedEntityPaths.clear();
    this._config.objects.forEach((mapping, mappingIndex) => {
      (mapping.relatedObjects ?? []).forEach((r) => {
        const key = this.getRelatedEntitySelectionKey(mappingIndex, r.objectName);
        this.selectedRelatedEntityPaths.set(key, r.relationSegment);
      });
    });
  }

  private clearRelatedEntitySelectionsForMapping(mappingIndex: number): void {
    const prefix = `${mappingIndex}:`;
    for (const key of this.selectedRelatedEntityPaths.keys()) {
      if (key.startsWith(prefix)) {
        this.selectedRelatedEntityPaths.delete(key);
      }
    }
    const mapping = this._config.objects[mappingIndex];
    if (mapping) {
      mapping.relatedObjects = [];
    }
  }

  private rebuildKnownObjectLookup(): void {
    this.knownObjectLookup.clear();
    (this.availableObjects ?? []).forEach((objectSummary) => {
      const canonical = this.getObjectApiName(objectSummary);
      if (!canonical) {
        return;
      }
      this.knownObjectLookup.set(canonical.toLowerCase(), canonical);
      const name = objectSummary.name?.trim();
      if (name) {
        this.knownObjectLookup.set(name.toLowerCase(), canonical);
      }
    });
  }

  private getKnownObjectName(objectName: string | undefined): string | undefined {
    const normalized = objectName?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    return this.knownObjectLookup.get(normalized);
  }

  private getObjectApiName(summary: Partial<ISalesforceObjectSummary> | undefined | null): string {
    const apiName = summary?.apiName?.trim();
    if (apiName) {
      return apiName;
    }
    const objectName = summary?.objectName?.trim();
    if (objectName) {
      return objectName;
    }
    return summary?.name?.trim() ?? '';
  }

  private mapSalesforceTypeToFieldDataType(sourceType?: string, segment?: string): string {
    const type = sourceType?.trim().toLowerCase() ?? '';
    const normalizedSegment = segment?.trim().toLowerCase() ?? '';
    if (!type) {
      if (normalizedSegment === 'id' || normalizedSegment.endsWith('id')) {
        return 'id';
      }
      return 'text';
    }
    if (type.includes('bool')) {
      return 'boolean';
    }
    if (type.includes('date') || type.includes('time')) {
      return 'date';
    }
    if (
      type.includes('int') ||
      type.includes('double') ||
      type.includes('currency') ||
      type.includes('percent') ||
      type.includes('decimal') ||
      type.includes('number')
    ) {
      return 'number';
    }
    if (type === 'id') {
      return 'id';
    }
    if (normalizedSegment === 'id' || normalizedSegment.endsWith('id')) {
      return 'id';
    }
    return 'text';
  }

  private scrollOpenFlyoutToEnd(): void {
    if (typeof document === 'undefined') {
      return;
    }
    requestAnimationFrame(() => {
      const dropdowns = document.querySelectorAll<HTMLElement>('.cdk-overlay-container .sf-path-dropdown');
      const activeDropdown = dropdowns.item(dropdowns.length - 1);
      if (!activeDropdown) {
        return;
      }
      activeDropdown.scrollLeft = activeDropdown.scrollWidth;
    });
  }

  private updateFlyoutScrollStateAsync(mappingIndex: number, fieldIndex: number, level: number): void {
    requestAnimationFrame(() => this.updateFlyoutScrollState(mappingIndex, fieldIndex, level));
  }

  private updateFlyoutScrollState(mappingIndex: number, fieldIndex: number, level: number): void {
    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    const dropdown = this.getPathDropdownElement(mappingIndex, fieldIndex, level);
    if (!dropdown) {
      state.flyoutCanScrollLeftByLevel[level] = false;
      state.flyoutCanScrollRightByLevel[level] = false;
      return;
    }
    state.flyoutCanScrollLeftByLevel[level] = dropdown.scrollLeft > 4;
    state.flyoutCanScrollRightByLevel[level] = dropdown.scrollLeft + dropdown.clientWidth < dropdown.scrollWidth - 4;
    this.cdr.markForCheck();
  }

  private getPathDropdownElement(mappingIndex: number, fieldIndex: number, level: number): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }
    const key = this.getPathLevelDomKey(mappingIndex, fieldIndex, level);
    return document.querySelector<HTMLElement>(`.cdk-overlay-container .sf-path-dropdown[data-path-level-key="${key}"]`);
  }

  private shouldResetPathBuilderState(previous: ISalesforceConfig, next?: ISalesforceConfig): boolean {
    const prevObjects = previous?.objects ?? [];
    const nextObjects = next?.objects ?? [];
    if (prevObjects.length !== nextObjects.length) {
      return true;
    }

    for (let i = 0; i < nextObjects.length; i += 1) {
      const prevMapping = prevObjects[i];
      const nextMapping = nextObjects[i];
      if (!prevMapping || !nextMapping) {
        return true;
      }
      if ((prevMapping.objectName ?? '') !== (nextMapping.objectName ?? '')) {
        return true;
      }
      if ((prevMapping.fields?.length ?? 0) !== (nextMapping.fields?.length ?? 0)) {
        return true;
      }
    }

    return false;
  }

  private rebuildExpandedFieldState(mappingIndex: number, nextFieldCount: number): void {
    const next = new Set<string>();
    this.expandedFieldKeys.forEach((key) => {
      const [map, field] = key.split(':').map((value) => Number.parseInt(value, 10));
      if (map !== mappingIndex || field < nextFieldCount) {
        next.add(key);
      }
    });
    this.expandedFieldKeys = next;
  }

  private getPathBuilderKey(mappingIndex: number, fieldIndex: number): string {
    return `${mappingIndex}:${fieldIndex}`;
  }

  private getProposedRelationDepth(mappingIndex: number, fieldIndex: number, level: number, columnIndex: number): number {
    const state = this.getPathBuilderState(mappingIndex, fieldIndex);
    const committedDepth = state.selections
      .slice(0, level)
      .filter((option): option is ISalesforcePathOption => Boolean(option))
      .filter((option) => this.isRelationOption(option)).length;
    // Each additional flyout column exists only after traversing one relation.
    // So columnIndex is the number of relation hops already traversed in this flyout.
    return committedDepth + columnIndex + 1;
  }

  private cloneConfig(config?: ISalesforceConfig): ISalesforceConfig {
    if (!config) {
      return { objects: [] };
    }
    return {
      objects:
        config.objects?.map((mapping) => ({
          id: mapping.id,
          label: mapping.label,
          objectName: mapping.objectName,
          enabled: mapping.enabled,
          fields:
            mapping.fields?.map((field) => ({
              label: field.label,
              path: field.path,
              dataType: field.dataType ?? 'text',
            })) ?? [],
          display: this.cloneDisplayTemplates(mapping.display),
          relatedObjects: mapping.relatedObjects?.map((r) => ({ objectName: r.objectName, relationSegment: r.relationSegment })),
        })) ?? [],
    };
  }

  private updateValidity(): void {

    this.isValid = this._config.objects.every((mapping, mappingIndex) => {
      const objectName = mapping.objectName?.trim();
      if (!objectName) {
        return true;
      }
      const primary = mapping.display?.primary?.trim();
      if (!primary) {
        return false;
      }
      // Block save if any field has no path selected or has an invalid saved path.
      const hasInvalidFieldPath = mapping.fields.some(
        (field, fieldIndex) => !field.path?.trim() || this.isBuiltPathInvalid(mappingIndex, fieldIndex),
      );
      if (hasInvalidFieldPath) {
        return false;
      }
      // Block save if any display template references tokens that don't match a field path.
      return this.getMappingInvalidTemplateTokens(mappingIndex).length <= 0;
    });
    this.validityChange.emit(this.isValid);
  }

  private extractTemplateTokens(template: string): string[] {
    const tokens: string[] = [];
    const regex = /\{\{\s*([^{}]+?)\s*\}\}/g;
    let match: RegExpExecArray | null = regex.exec(template ?? '');
    while (match) {
      const token = match[1]?.trim();
      if (token) {
        tokens.push(token);
      }
      match = regex.exec(template ?? '');
    }
    return tokens;
  }

  private getTemplateInvalidTokensForMapping(
    mapping: ISalesforceObjectMapping,
    templateKey: 'primary' | 'secondary'
  ): string[] {
    const template = mapping.display?.[templateKey] ?? '';
    const tokens = this.extractTemplateTokens(template);
    if (!tokens.length) {
      return [];
    }

    const validPaths = new Set(
      (mapping.fields ?? [])
        .map((field) => field.path?.trim() ?? '')
        .filter((path) => Boolean(path))
    );
    return Array.from(new Set(tokens.filter((token) => !validPaths.has(token))));
  }

  private getMappingInvalidTemplateTokens(mappingIndex: number): string[] {
    const mapping = this._config.objects[mappingIndex];
    if (!mapping) {
      return [];
    }

    const primaryInvalid = this.getTemplateInvalidTokensForMapping(mapping, 'primary');
    const secondaryInvalid = this.getTemplateInvalidTokensForMapping(mapping, 'secondary');
    return Array.from(new Set([...primaryInvalid, ...secondaryInvalid]));
  }

  private getEmptyDisplayTemplates(): ISalesforceDisplayTemplates {
    return {
      primary: '',
      secondary: '',
    };
  }

  private cloneDisplayTemplates(display?: ISalesforceDisplayTemplates): ISalesforceDisplayTemplates {
    return {
      primary: display?.primary ?? '',
      secondary: display?.secondary ?? '',
    };
  }

  private scrollToLastMapping(): void {
    setTimeout(() => {
      const el = this.mappingCards?.last?.nativeElement;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }
}
